import { Fragment, useCallback, useState } from "react";

import { Empty, FoldablePanel, Note, Panel } from "./Panel";
import { PnLShape } from "./PnLShape";
import { RateCell, RateHead } from "./RateCell";
import { useJournalShapes } from "../hooks/useShapes";
import { fragility, fragilityTone, rateTone, rateWord, shapeOf } from "../lib/shape";
import { useShiftBreakdown, useShiftSignals, useShiftSummary } from "../hooks/useShift";
import type { ShadowShape, ShiftCell, ShiftSignal } from "../api/types";
import {
  MIN_SETTLED,
  amount,
  cellKey,
  fillPrice,
  minuteOf,
  order,
  overshoot,
  sliceKey,
  sliceLabel,
  sliceView,
  slicesFor,
  thresholdLabel,
  trustworthy,
  view,
  type ShiftView,
} from "../lib/shift";

/**
 * Покупка смещённого края.
 *
 * # Зачем панель на экране
 *
 * Вопрос оператора: если купить сместившуюся сторону по 0.63, как часто она
 * выигрывает — и меняется ли ответ от минуты окна. Арифметика говорит, где вся
 * ценность: комиссия на цене 0.63 равна 1.63 ¢ на share, значит вход окупается с
 * 64.6 % угаданных, а по одному знаку «цена против страйка» уже известно 64–65 %
 * на первой минуте окна и 91–92 % на четвёртой.
 *
 * Отсюда устройство панели. Строка — КЛЕТКА «ступень × минута», а не итог: итог
 * сравнивался бы с планкой, которой не было ни в одну минуту. И рядом с долей
 * угаданного стоят ТРИ линии сравнения, а не одна прибыль:
 *
 *   - цена налива — есть ли кромка вообще;
 *   - вероятность замка — есть ли кромка СВЕРХ того, что и так выигрывало;
 *   - измеренная базовая линия — бьёт ли сигнал планку, которую не побил ни один
 *     проверенный признак.
 *
 * # Чего на панели НЕТ и не будет
 *
 * Кнопок. У подсистемы нет порта постановки ордера — не «выключен флагом», а нет
 * в зависимостях вовсе, — и кнопка по строке, за которой не может стоять ордер,
 * обещала бы действие, которого не существует.
 *
 * Строк секундного обхода. Они писались по цене, до которой аск успевал уехать
 * за такт, то есть мерили наше запаздывание, а не рынок; цена запаздывания
 * измерена, обход переведён на стакан, и журнал читается только по строкам
 * `book` (срез в storage.Shift). Колонка «съём» ушла вместе с ними: подпись,
 * одинаковая у каждой строки, — не ответ, а шум.
 *
 * # Разворот строки: подтвердило ли расхождение ту же сторону
 *
 * Клетка складывает входы, которые сигнал считает одинаковыми, а замер
 * 2026-09-01 развёл их вдвое. По 1684 окно-сторонам BTC 5m: входы, у которых
 * расхождение модели назвало ТУ ЖЕ сторону за минуту до касания, дали 64.5 %
 * угаданного при наливе 0.591 (+3.7 п.п. над ценой), молчание модели — 58.3 %
 * при наливе 0.629 (−5.2 п.п.). Признак чужой — сигнал смещения чисто ценовой, —
 * и потому приносит сведения, которых в строке нет.
 *
 * Вторая ось разворота — РАЗВОРОТ окна, и она обязательна: подтверждённые входы
 * на три четверти лежат в развернувшихся окнах, и без этой оси разрез сравнивал
 * бы подтверждение с базовой линией другой популяции.
 */
export function ShiftPanel({ hours, windowLen }: { hours: number; windowLen: number }) {
  const summary = useShiftSummary(hours, windowLen);
  const cells = summary.data?.cells ?? [];
  // Форма счёта — общим запросом на все журналы: границы корзин обязаны быть
  // одни, иначе гистограммы соседних панелей перестанут сравниваться.
  const shapes = useJournalShapes(hours, "shift", windowLen);
  // Развёрнутые клетки — Set по ключу клетки, а не флаг в строке: разворот
  // обязан пережить перестройку списка при опросе. Тот же приём, что у сводки
  // расхождения и у состава пула семей.
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const toggle = useCallback((key: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);

  return (
    <Panel title="Смещённый край" subtitle={cells.length ? headline(cells) : undefined}>
      {!summary.data ? (
        <Empty>{summary.isError ? "сводка не загрузилась" : "загрузка…"}</Empty>
      ) : cells.length === 0 ? (
        <Empty>
          входов ещё не было: строка рождается, когда цена стороны впервые доходит
          до ступени лесенки при открытом окне
        </Empty>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-[var(--color-muted)]">
                <tr>
                  <th className="w-6 pb-2" />
                  <th className="pb-2 font-normal">окно</th>
                  <th className="pb-2 font-normal">ступень</th>
                  <th className="pb-2 font-normal">минута</th>
                  <th className="pb-2 text-right font-normal">входов</th>
                  <th className="pb-2 text-right font-normal">угадано</th>
                  <th className="pb-2 text-right font-normal">без разворотов</th>
                  <th className="pb-2 text-right font-normal">цена</th>
                  <th className="pb-2 text-right font-normal">над ценой</th>
                  <th className="pb-2 text-right font-normal">над физикой</th>
                  <th className="pb-2 text-right font-normal">над базой</th>
                  <th className="pb-2 text-right font-normal">по порогу</th>
                  <th className="pb-2 text-right font-normal">откаты</th>
                  <th className="pb-2 text-right font-normal">счёт</th>
                  <RateHead />
                  <th className="pb-2 text-right font-normal">форма счёта</th>
                </tr>
              </thead>
              <tbody>
                {order(cells).map((c) => {
                  const key = cellKey(c);
                  return (
                    <Fragment key={key}>
                      <Row
                        v={view(c)}
                        hours={hours}
                        shape={shapeOf(shapes, key)}
                        expanded={open.has(key)}
                        onToggle={() => toggle(key)}
                      />
                      {open.has(key) && (
                        <BreakdownRow
                          cell={c}
                          hours={hours}
                          windowLen={windowLen}
                          colSpan={16}
                        />
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Приговор форме — только по клеткам, где сделок хватает: на пяти
              входах «хрупкий плюс» и «ровный плюс» неразличимы, и объявлять
              что-либо значило бы обещать вывод, которого нет. */}
          <Note label="ставка и хрупкость — по клеткам">
            <ul className="space-y-1 text-[11px] leading-relaxed text-[var(--color-muted)]">
              {cells
                .filter((c) => c.settled >= MIN_SETTLED)
                .map((c) => {
                  const key = cellKey(c);
                  const shape = shapeOf(shapes, key);
                  return (
                    <li key={key}>
                      <b>
                        {thresholdLabel(c.threshold_bp)} · {c.minute}-я мин
                      </b>{" "}
                      — <b style={{ color: rateTone(shape, hours) }}>{rateWord(shape, hours)}</b>;{" "}
                      <span style={{ color: fragilityTone(shape) }}>{fragility(shape)}</span>.
                    </li>
                  );
                })}
            </ul>
          </Note>
          <Note label="как читать: цена, база по минутам и порог доверия">
            «цена» — средний налив с комиссией, то есть безубыток клетки. «над
            базой» считается только для пятиминуток: измеренная линия 64–65 % на
            1-й минуте, 69–70 % на 2-й, 80 % на 3-й, 91–92 % на 4-й (замер
            2026-08-26). Клетки тусклее {MIN_SETTLED} сведённых читать нельзя:
            там разница в десять пунктов не отличается от монетки.{" "}
            <b>Значок ▸ разворачивает клетку в разрез</b> «подтвердило ли
            расхождение модели ту же сторону × развернулось ли окно»: входы внутри
            клетки неравноценны, и на замере признак стои́т девяти пунктов кромки.
            Ордеров за строками нет — у подсистемы нет порта постановки.
          </Note>
        </>
      )}
    </Panel>
  );
}

/** Строка клетки. */
function Row({
  v,
  shape,
  hours,
  expanded,
  onToggle,
}: {
  v: ShiftView;
  shape: ShadowShape | undefined;
  hours: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  const c = v.cell;
  // Ненадёжная клетка гасится ЦЕЛИКОМ, а не помечается значком: тусклая строка
  // не зовёт читать её числа, а значок рядом с крупной цифрой проигрывает ей.
  const dim = trustworthy(c) ? "" : " text-[var(--color-muted)]";
  return (
    <tr className={`border-t border-[var(--color-border)]${dim}`}>
      {/* Разворот — тем же значком, что у сводки расхождения: под строкой лежит
          её разрез по подтверждению модели и развороту окна. */}
      <td className="py-1.5">
        <button
          type="button"
          onClick={onToggle}
          className="text-[var(--color-muted)] hover:text-[var(--color-fg)]"
          aria-expanded={expanded}
          title="равноценны ли входы: подтвердило ли расхождение ту же сторону"
        >
          {expanded ? "▾" : "▸"}
        </button>
      </td>
      <td className="py-1.5">{windowWord(c.window_len_s)}</td>
      <td className="py-1.5 num">{thresholdLabel(c.threshold_bp)}</td>
      <td className="py-1.5">м{c.minute}</td>
      <td className="py-1.5 text-right num">{c.signals}</td>
      <td className="py-1.5 text-right num">
        {pct(v.winRate)} <span className="text-[var(--color-muted)]">{c.won}/{c.settled}</span>
      </td>
      <td className="py-1.5 text-right num">
        {pct(v.firstWinRate)}{" "}
        <span className="text-[var(--color-muted)]">
          {c.first_won}/{c.first_settled}
        </span>
      </td>
      <td className="py-1.5 text-right num">{num(v.priceLine, 3)}</td>
      <td className={`py-1.5 text-right num ${tone(v.edgeVsPrice)}`}>{pp(v.edgeVsPrice)}</td>
      <td className={`py-1.5 text-right num ${tone(v.edgeVsPhys)}`}>{pp(v.edgeVsPhys)}</td>
      <td className={`py-1.5 text-right num ${tone(v.edgeVsBaseline)}`}>
        {pp(v.edgeVsBaseline)}
      </td>
      <td className="py-1.5 text-right num">{pct(v.limitShare)}</td>
      <td className="py-1.5 text-right num">{pct(v.retracedShare)}</td>
      <td className={`py-1.5 text-right num ${tone(v.pnl)}`}>{num(v.pnl, 2)}</td>
      {/* Удельный счёт — ВПРИТЫК к сумме. У этой панели клеток полсотни, и
          подпись под таблицей до читателя не доезжает: замер разметки дал
          ставку на 16 604-м символе из 20 545. Гашение передаётся ставке
          отдельно: её inline-цвет иначе перебил бы тусклый класс строки. */}
      <RateCell shape={shape} hours={hours} dim={dim !== ""} />
      {/* Форма СЧЁТА: сумма слева отвечает «сколько», гистограмма — «из чего».
          У этой подсистемы весь размах лежит в [−5, +4], поэтому крайние корзины
          пусты всегда — границы общие на все журналы намеренно, чтобы формы
          сравнивались с соседними панелями. */}
      <td className="py-1.5 text-right">
        <PnLShape shape={shape} />
      </td>
    </tr>
  );
}

/**
 * Разрез клетки: равноценны ли входы внутри неё.
 *
 * # Что здесь спрашивается
 *
 * Клетка «ступень × минута» складывает входы, которые сигнал считает
 * одинаковыми: цена дошла до ступени — вошли. Замер 2026-09-01 развёл их вдвое
 * по признаку, которого в самой строке нет, — назвало ли РАСХОЖДЕНИЕ МОДЕЛИ ту
 * же сторону незадолго до касания.
 *
 * Строки идут парами «подтверждено / молчала» внутри одного состояния разворота:
 * так соседние отличаются РОВНО одним признаком. Иначе сравнивалась бы не та
 * пара — подтверждённые входы на три четверти лежат в развернувшихся окнах, а у
 * тех своя базовая линия.
 *
 * # Почему у корзины СВОЯ цена
 *
 * Потому что подтверждённые входы наливаются дешевле (0.591 против 0.629 на
 * замере), а безубыток тейкера целиком определяется наливом. Доля угаданного,
 * сравнённая с общей планкой клетки, перевернула бы знак кромки у обеих корзин.
 *
 * # Почему хук живёт здесь
 *
 * Компонент монтируется только под РАЗВЁРНУТОЙ строкой, значит свёрнутая панель
 * разреза не опрашивает вовсе — тот же приём, что у тел FoldablePanel. Ручка
 * одна на все клетки, и две развёрнутые строки складываются в один запрос
 * ключом TanStack.
 */
function BreakdownRow({
  cell,
  hours,
  windowLen,
  colSpan,
}: {
  cell: ShiftCell;
  hours: number;
  windowLen: number;
  colSpan: number;
}) {
  const breakdown = useShiftBreakdown(hours, windowLen);
  const slices = breakdown.data?.slices;
  const rows = slices ? slicesFor(slices, cell) : [];
  return (
    <tr className="border-t border-[var(--color-border)]">
      <td colSpan={colSpan} className="bg-[var(--color-bg)] px-3 py-2">
        {!slices ? (
          <Empty>{breakdown.isError ? "разрез не загрузился" : "загрузка…"}</Empty>
        ) : rows.length === 0 ? (
          <p className="text-[11px] text-[var(--color-muted)]">корзин по этой клетке нет</p>
        ) : (
          <>
            <table className="text-[11px]">
              <thead className="text-left text-[var(--color-muted)]">
                <tr>
                  <th className="pb-1 pr-4 font-normal">корзина</th>
                  <th className="pb-1 pr-4 text-right font-normal">входов</th>
                  <th className="pb-1 pr-4 text-right font-normal">угадано</th>
                  <th className="pb-1 pr-4 text-right font-normal">цена</th>
                  <th className="pb-1 pr-4 text-right font-normal">над ценой</th>
                  <th className="pb-1 text-right font-normal">счёт</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => {
                  const v = sliceView(s);
                  return (
                    <tr key={sliceKey(s)} className="border-t border-[var(--color-border)]">
                      <td className="whitespace-nowrap py-0.5 pr-4">{sliceLabel(s)}</td>
                      <td className="num py-0.5 pr-4 text-right">{s.signals}</td>
                      <td className="num py-0.5 pr-4 text-right">
                        {pct(v.winRate)}{" "}
                        <span className="text-[var(--color-muted)]">
                          {s.won}/{s.settled}
                        </span>
                      </td>
                      <td className="num py-0.5 pr-4 text-right">{num(v.priceLine, 3)}</td>
                      <td className={`num py-0.5 pr-4 text-right ${tone(v.edgeVsPrice)}`}>
                        {pp(v.edgeVsPrice)}
                      </td>
                      <td className={`num whitespace-nowrap py-0.5 text-right ${tone(v.pnl)}`}>
                        {num(v.pnl, 2)}{" "}
                        <span className="text-[var(--color-muted)]">по {s.pnl_priced}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {/* Ширина окна подтверждения приезжает С СЕРВЕРА: вторая её копия
                здесь разошлась бы с разрезом молча при первой калибровке. И
                оговорка про знаменатель — обязательная: «модель молчала»
                означает и «подсистема стояла в off», а это разные ответы. */}
            <p className="mt-1 text-[11px] leading-relaxed text-[var(--color-muted)]">
              «подтверждено» — расхождение модели назвало ту же сторону не раньше
              чем за {breakdown.data?.confirm_s ?? "—"} с до входа, и только ДО
              него: сигнал после входа — метка отката цены, а не предсказание.
              «модель молчала» включает и молчащую подсистему расхождения, так что
              пустая корзина подтверждений — вопрос к соседнему журналу, а не
              ответ.
            </p>
          </>
        )}
      </td>
    </tr>
  );
}

/**
 * Заголовок панели: что в этой выборке главное.
 *
 * Называется КЛЕТКА, а не итог, и обязательно со знаменателем: «лучшая клетка
 * +8 п.п.» на четырёх строках и на четырёхстах — разные утверждения.
 */
function headline(cells: ShiftCell[]): string {
  const надёжные = cells.filter(trustworthy).map(view);
  if (надёжные.length === 0) {
    return `клеток с ${MIN_SETTLED}+ сведёнными пока нет — судить рано`;
  }
  const лучшая = надёжные.reduce((best, v) =>
    (v.edgeVsPrice ?? -1) > (best.edgeVsPrice ?? -1) ? v : best,
  );
  const c = лучшая.cell;
  return (
    `лучшая клетка ${thresholdLabel(c.threshold_bp)} на минуте ${c.minute}: ` +
    `${pp(лучшая.edgeVsPrice)} над ценой на ${c.settled} сведённых`
  );
}

function windowWord(sec: number): string {
  return sec % 60 === 0 ? `${sec / 60}м` : `${sec}с`;
}

/** Доля процентами. Прочерк вместо нуля: «нечего делить» — не «ноль». */
function pct(v: number | null): string {
  return v === null ? "—" : `${(100 * v).toFixed(1)} %`;
}

/** Кромка в процентных пунктах, со знаком. */
function pp(v: number | null): string {
  return v === null ? "—" : `${v >= 0 ? "+" : ""}${(100 * v).toFixed(1)}`;
}

function num(v: number | null, digits: number): string {
  return v === null ? "—" : v.toFixed(digits);
}

/**
 * Цвет знака. Те же токены, что у соседних панелей: покупка — «бид», продажа —
 * «аск». Ноль и прочерк нейтральны: ни то, ни другое не новость.
 */
function tone(v: number | null): string {
  if (v === null || v === 0) return "";
  return v > 0 ? "text-[var(--color-bid)]" : "text-[var(--color-ask)]";
}

/**
 * Лента теневых входов по смещению.
 *
 * # Зачем она рядом со сводкой
 *
 * Сводка отвечает средним по клетке, а вопрос «почему вход по ступени 0.55
 * наливается по 0.66» средним не отвечается: там видно только результат, а не
 * его причина. Лента показывает по строке на вход, и рядом стоят три числа,
 * которые и есть ответ: порог, фактический налив и ПЕРЕЛЁТ между ними.
 *
 * Перелёт — не украшение. Комиссия тейкера равна `размер × 0.07 × p × (1 − p)`,
 * поэтому безубыток целиком определяется ценой налива: та же доля угаданных
 * (0.602) убыточна на наливе 0.66 и прибыльна на 0.56. Пока обход шёл раз в
 * секунду, перелёт доходил до девяти центов — и знак всего замера определялся
 * им, а не предсказанием.
 *
 * # Почему тело — отдельный компонент
 *
 * Под FoldablePanel неразвёрнутое тело НЕ МОНТИРУЕТСЯ, а значит хук не заводит
 * опроса вовсе: свёрнутая лента молчит, а не просто прячет таблицу. Тот же приём
 * и тот же довод, что у ленты расхождения.
 *
 * # Кнопок здесь нет и не будет
 *
 * У подсистемы нет порта постановки ордера — не «выключен флагом», а нет в
 * зависимостях вовсе (см. шапку панели).
 */
export function ShiftFeed({ hours, windowLen }: { hours: number; windowLen: number }) {
  return (
    <FoldablePanel title="Входы по смещению" subtitle={`за ${hours} ч, по строке на вход`}>
      <ShiftFeedBody hours={hours} windowLen={windowLen} />
    </FoldablePanel>
  );
}

function ShiftFeedBody({ hours, windowLen }: { hours: number; windowLen: number }) {
  const feed = useShiftSignals(hours, undefined, windowLen);
  const rows = feed.data?.signals ?? [];

  if (!feed.data) {
    return <Empty>{feed.isError ? "лента не загрузилась" : "загрузка…"}</Empty>;
  }
  if (rows.length === 0) {
    return <Empty>входов за период не было</Empty>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-[var(--color-panel)]">
          <tr className="text-left text-[var(--color-muted)]">
            <th className="pb-2 font-normal">когда</th>
            <th className="pb-2 font-normal">окно</th>
            <th className="pb-2 font-normal">исход</th>
            <th className="pb-2 text-right font-normal">ступень</th>
            <th className="pb-2 text-right font-normal">аск</th>
            <th className="pb-2 text-right font-normal">налив</th>
            {/* Три колонки подряд и есть весь ответ: порог, что заплатили,
                насколько промахнулись. */}
            <th className="pb-2 text-right font-normal">перелёт</th>
            <th className="pb-2 text-right font-normal">слепое окно</th>
            <th className="pb-2 font-normal">по порогу</th>
            <th className="pb-2 font-normal">что стало</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <FeedRow key={s.id} s={s} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FeedRow({ s }: { s: ShiftSignal }) {
  const fill = fillPrice(s);
  const slip = overshoot(s);
  const ask = amount(s.book_ask);
  return (
    <tr className="border-t border-[var(--color-border)]">
      <td className="py-1 whitespace-nowrap">{clock(s.detected_at)}</td>
      <td className="py-1 whitespace-nowrap">
        {windowWord(s.window_len_s)} · м{minuteOf(s)}
      </td>
      <td className="py-1">
        {s.outcome_name ?? s.asset_id.slice(0, 8)}
        {/* Разворот пометкой, а не фильтром: «сколько раз окно
            переворачивалось после смещения» — та величина, которой измеряется
            риск затеи, и спрятав её, мы получили бы долю угаданного без
            знаменателя. */}
        {s.reversal ? <span className="text-[var(--color-muted)]"> · разворот</span> : null}
      </td>
      <td className="py-1 text-right num">{thresholdLabel(s.threshold_bp)}</td>
      <td className="py-1 text-right num">{num(ask, 3)}</td>
      <td className="py-1 text-right num">{num(fill, 3)}</td>
      <td className={`py-1 text-right num ${slipTone(slip)}`}>{signed(slip)}</td>
      <td className="py-1 text-right num text-[var(--color-muted)]">
        {s.sample_ms === undefined ? "—" : `${s.sample_ms} мс`}
      </td>
      {/* Отказ плана «по порогу» — РЕЗУЛЬТАТ замера, а не пропуск строки: он и
          отвечает на «а можно ли было взять ровно по порогу». */}
      <td className="py-1">{s.limit.reason ? "нет" : "да"}</td>
      <td className="py-1">{outcomeWord(s)}</td>
    </tr>
  );
}

/** Время без даты: лента живёт часами, и дата в каждой строке — шум. */
function clock(raw: string): string {
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString();
}

/** Перелёт со знаком, в центах: сотые доллара читаются глазом быстрее долей. */
function signed(v: number | null): string {
  if (v === null) return "—";
  const cents = 100 * v;
  return `${cents >= 0 ? "+" : ""}${cents.toFixed(1)} ¢`;
}

/**
 * Цвет перелёта. Переплата — «аск», потому что это ровно она: заплатили дороже,
 * чем собирались. Ноль и прочерк нейтральны.
 */
function slipTone(v: number | null): string {
  if (v === null || v === 0) return "";
  return v > 0 ? "text-[var(--color-ask)]" : "text-[var(--color-bid)]";
}

/**
 * Чем кончилось. «Ждём» отличается от «не выиграл» намеренно: несведённая строка
 * ни в одну сумму не входит, и показывать её проигрышем значило бы объявлять
 * результат до его получения.
 */
function outcomeWord(s: ShiftSignal): string {
  if (!s.resolved) return "ждём";
  return s.won ? "выиграл" : "проиграл";
}
